import { formatPercentage } from '@/utils/formatting/format-percentage';
import { getCurrencyDetails } from '@/utils/get-currency-details';
import { Banknote, BarChart4, ExternalLink, ShieldCheck } from 'lucide-react';
import { Button } from '../../../design-system/button/button';
import { Inline } from '../../../design-system/inline/inline';
import { Separator } from '../../../design-system/separator/separator';
import { Stack } from '../../../design-system/stack/stack';
import { HoldingDetails } from '../types/treasury-types';

type TreasuryEmptyStateProps = {
  holdingDetails: HoldingDetails;
  onLearnMore: () => void;
  onContinue: () => void;
};

export const TreasuryEmptyState = ({ holdingDetails, onLearnMore, onContinue }: TreasuryEmptyStateProps) => {
  const { flag: currencyFlag, plural: currencyName } = getCurrencyDetails(holdingDetails.currency);

  const formattedNetYield = formatPercentage(holdingDetails.netYield);

  return (
    <Stack space={8}>
      <Stack space={4}>
        <img src={currencyFlag} alt="" className="h-12 w-12 rounded-full" />

        <h2 className="text-xl leading-7 font-semibold text-black">Put your {currencyName.toLowerCase()} to work</h2>

        <Stack space={1}>
          <p className="text-3xl leading-none font-semibold text-success">{formattedNetYield}</p>

          <p className="text-sm leading-5 font-medium text-success">Annual variable rate</p>
        </Stack>
      </Stack>

      <Separator />

      <Stack space={6}>
        <Inline space={4} align="start">
          <BarChart4 size={24} className="text-muted-foreground" />

          <Stack space={2} className="flex-1">
            <h3 className="text-sm leading-5 font-medium text-black">Invest your idle cash</h3>

            <p className="text-sm leading-5 font-normal text-muted-foreground">
              Earn returns daily on {holdingDetails.currency} by investing in a fund that holds short term government
              assets.
            </p>
          </Stack>
        </Inline>

        <Inline space={4} align="start">
          <Banknote size={24} className="text-muted-foreground" />

          <Stack space={2} className="flex-1">
            <h3 className="text-sm leading-5 font-medium text-black">Keep your money accessible</h3>

            <p className="text-sm leading-5 font-normal text-muted-foreground">
              No lock-up periods. Withdrawals settle same-day or next-day, so your money stays accessible for payroll,
              bills, or new opportunities.
            </p>
          </Stack>
        </Inline>

        <Inline space={4} align="start">
          <ShieldCheck size={24} className="text-muted-foreground" />

          <Stack space={2} className="flex-1">
            <h3 className="text-sm leading-5 font-medium text-black">Managed by BlackRock</h3>

            <p className="text-sm leading-5 font-normal text-muted-foreground">
              AAA-rated money market funds managed by BlackRock, one of the world's largest asset managers, and held in
              your company's name. Money is invested in{' '}
              <a
                href={holdingDetails.moreInfoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground underline decoration-solid underline-offset-auto"
              >
                {holdingDetails.name}
              </a>
              .
            </p>
          </Stack>
        </Inline>

        <p className="text-sm leading-5 font-normal text-pretty text-muted-foreground">
          Capital at risk. Current rate is variable and based on 7-day performance as of{' '}
          <span className="whitespace-nowrap">{holdingDetails.formattedUpdatedAtByFundManager}</span>
          {`.`}
        </p>
      </Stack>

      <div className="flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-start">
        <Button variant="secondary" onClick={onLearnMore} iconRight={<ExternalLink size={16} />}>
          Learn more
        </Button>

        <Button variant="default" onClick={onContinue}>
          Continue
        </Button>
      </div>
    </Stack>
  );
};
